/**
 * Decision log: architecture decisions persisted in the project as one
 * Markdown file per decision (ADR-lite) under .elyra/memory/decisions/.
 *
 * Why one file per decision: append-only single files collide the moment two
 * developers record decisions in the same week. Separate files never conflict
 * in git and match the ADR convention people already know.
 *
 * Decisions are never deleted - a newer decision supersedes an older one, so
 * the history of "why did we switch back?" stays intact.
 */

import { existsSync, mkdirSync, readdirSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import { CONFIG_DIR_NAME } from "../config.js";

export type DecisionStatus = "active" | "superseded";

export interface Decision {
	/** Stable id, also the file stem: YYYY-MM-DD-slug. */
	id: string;
	title: string;
	/** ISO date (YYYY-MM-DD). */
	date: string;
	status: DecisionStatus;
	/** Id of the decision this one replaces. */
	supersedes?: string;
	/** Id of the decision that replaced this one (set when superseded). */
	supersededBy?: string;
	/** Session id the decision was recorded in, when known. */
	session?: string;
	/** Why a decision was needed. */
	context: string;
	/** What was chosen. */
	decision: string;
	/** Alternatives considered and why they were rejected. */
	alternatives?: string;
	/** Consequences and constraints on future work. */
	consequences?: string;
}

export interface DecisionInput {
	title: string;
	context: string;
	decision: string;
	alternatives?: string;
	consequences?: string;
	supersedes?: string;
	session?: string;
	/** Override the date (tests). Defaults to today. */
	date?: string;
}

export interface WriteDecisionResult {
	decision: Decision;
	path: string;
	/** The decision that was marked superseded, if any. */
	superseded?: Decision;
}

/** Pluggable filesystem operations (overridable for tests). */
export interface DecisionStoreOperations {
	readDir: (dir: string) => string[];
	readFile: (path: string) => string;
	writeFile: (path: string, content: string) => void;
	mkdir: (dir: string) => void;
	exists: (path: string) => boolean;
}

const defaultOperations: DecisionStoreOperations = {
	readDir: (dir) => readdirSync(dir),
	readFile: (path) => readFileSync(path, "utf-8"),
	writeFile: (path, content) => writeFileSync(path, content, "utf-8"),
	mkdir: (dir) => mkdirSync(dir, { recursive: true }),
	exists: (path) => existsSync(path),
};

const MAX_SLUG_LENGTH = 60;
/** Digest budget: keep the system prompt section small even for large logs. */
const DIGEST_MAX_DECISIONS = 30;
const DIGEST_MAX_LINE_LENGTH = 160;

export function getDecisionsDir(cwd: string): string {
	return join(cwd, CONFIG_DIR_NAME, "memory", "decisions");
}

export function getDecisionPath(cwd: string, id: string): string {
	return join(getDecisionsDir(cwd), `${id}.md`);
}

/** Turn a title into a filesystem- and URL-safe slug. */
export function slugify(title: string): string {
	const slug = title
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, MAX_SLUG_LENGTH)
		.replace(/-+$/g, "");
	return slug || "decision";
}

function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

function yamlString(value: string): string {
	const oneLine = value.replace(/\r?\n/g, " ").trim();
	return `"${oneLine.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function unquoteYaml(value: string): string {
	const trimmed = value.trim();
	if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2) {
		return trimmed.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, "\\");
	}
	return trimmed;
}

function normalizeBlock(text: string | undefined): string {
	return (text ?? "").replace(/^\n+/, "").replace(/\s+$/, "");
}

/** Render a decision as Markdown with YAML frontmatter. */
export function formatDecisionMarkdown(decision: Decision): string {
	const fm: string[] = ["---", `id: ${decision.id}`, `title: ${yamlString(decision.title)}`, `date: ${decision.date}`];
	fm.push(`status: ${decision.status}`);
	if (decision.supersedes) fm.push(`supersedes: ${decision.supersedes}`);
	if (decision.supersededBy) fm.push(`supersededBy: ${decision.supersededBy}`);
	if (decision.session) fm.push(`session: ${decision.session}`);
	fm.push("---", "");

	const sections: string[] = [`# ${decision.title}`, "", "## Context", "", normalizeBlock(decision.context), ""];
	sections.push("## Decision", "", normalizeBlock(decision.decision), "");
	if (decision.alternatives?.trim()) {
		sections.push("## Alternatives considered", "", normalizeBlock(decision.alternatives), "");
	}
	if (decision.consequences?.trim()) {
		sections.push("## Consequences", "", normalizeBlock(decision.consequences), "");
	}

	return `${fm.join("\n")}\n${sections.join("\n")}`;
}

/**
 * Parse a decision file. Returns undefined for files that are not decisions
 * (missing frontmatter or required fields) so stray files are ignored.
 */
export function parseDecisionMarkdown(content: string): Decision | undefined {
	const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(content);
	if (!match) return undefined;
	const [, frontmatter, body] = match;

	const fields: Record<string, string> = {};
	for (const line of frontmatter.split(/\r?\n/)) {
		const idx = line.indexOf(":");
		if (idx === -1) continue;
		fields[line.slice(0, idx).trim()] = unquoteYaml(line.slice(idx + 1));
	}

	if (!fields.id || !fields.title || !fields.date) return undefined;
	const status: DecisionStatus = fields.status === "superseded" ? "superseded" : "active";

	const sections = parseSections(body);

	return {
		id: fields.id,
		title: fields.title,
		date: fields.date,
		status,
		supersedes: fields.supersedes || undefined,
		supersededBy: fields.supersededBy || undefined,
		session: fields.session || undefined,
		context: sections.context ?? "",
		decision: sections.decision ?? "",
		alternatives: sections["alternatives considered"] ?? sections.alternatives,
		consequences: sections.consequences,
	};
}

function parseSections(body: string): Record<string, string> {
	const result: Record<string, string> = {};
	let current: string | undefined;
	const buffer: string[] = [];
	const flush = () => {
		if (current !== undefined) {
			result[current] = buffer.join("\n").trim();
		}
		buffer.length = 0;
	};
	for (const line of body.split(/\r?\n/)) {
		const heading = /^## (.+)$/.exec(line);
		if (heading) {
			flush();
			current = heading[1].trim().toLowerCase();
			continue;
		}
		if (current !== undefined) buffer.push(line);
	}
	flush();
	return result;
}

/** Load all decisions for a project, sorted by date then id. */
export function listDecisions(cwd: string, ops: DecisionStoreOperations = defaultOperations): Decision[] {
	const dir = getDecisionsDir(cwd);
	if (!ops.exists(dir)) return [];

	const decisions: Decision[] = [];
	for (const file of ops.readDir(dir)) {
		if (!file.endsWith(".md")) continue;
		try {
			const parsed = parseDecisionMarkdown(ops.readFile(join(dir, file)));
			if (parsed) decisions.push(parsed);
		} catch {
			// Unreadable file: skip rather than fail the whole log.
		}
	}

	return decisions.sort((a, b) => (a.date === b.date ? a.id.localeCompare(b.id) : a.date.localeCompare(b.date)));
}

function normalizeTitle(title: string): string {
	return title
		.toLowerCase()
		.replace(/[^a-z0-9 ]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

/**
 * Find an existing decision with (near-)identical title. Used to keep
 * automatic extraction from re-recording the same decision after every
 * compaction.
 */
export function findSimilarDecision(decisions: readonly Decision[], title: string): Decision | undefined {
	const wanted = normalizeTitle(title);
	if (!wanted) return undefined;
	return decisions.find((d) => normalizeTitle(d.title) === wanted);
}

/**
 * Write a new decision. If `supersedes` is given and that decision exists,
 * it is rewritten with status superseded and a back-reference.
 */
export function writeDecision(
	cwd: string,
	input: DecisionInput,
	ops: DecisionStoreOperations = defaultOperations,
): WriteDecisionResult {
	const title = input.title.trim();
	if (!title) throw new Error("Decision title is required.");
	if (!input.decision?.trim()) throw new Error("Decision text is required.");
	if (!input.context?.trim()) throw new Error("Decision context is required.");

	const dir = getDecisionsDir(cwd);
	if (!ops.exists(dir)) ops.mkdir(dir);

	const date = input.date ?? todayIso();
	const baseId = `${date}-${slugify(title)}`;
	let id = baseId;
	let counter = 2;
	while (ops.exists(join(dir, `${id}.md`))) {
		id = `${baseId}-${counter++}`;
	}

	let superseded: Decision | undefined;
	if (input.supersedes) {
		const existing = listDecisions(cwd, ops).find((d) => d.id === input.supersedes);
		if (!existing) {
			throw new Error(`Cannot supersede unknown decision "${input.supersedes}".`);
		}
		superseded = { ...existing, status: "superseded", supersededBy: id };
		ops.writeFile(join(dir, `${existing.id}.md`), formatDecisionMarkdown(superseded));
	}

	const decision: Decision = {
		id,
		title,
		date,
		status: "active",
		supersedes: input.supersedes,
		session: input.session,
		context: input.context,
		decision: input.decision,
		alternatives: input.alternatives,
		consequences: input.consequences,
	};

	const path = join(dir, `${id}.md`);
	ops.writeFile(path, formatDecisionMarkdown(decision));

	return { decision, path, superseded };
}

function firstLine(text: string, max: number): string {
	const line =
		text
			.split(/\r?\n/)
			.find((l) => l.trim().length > 0)
			?.trim() ?? "";
	return line.length > max ? `${line.slice(0, max - 1)}\u2026` : line;
}

/**
 * Compact digest of active decisions for the system prompt: title plus a
 * one-line decision, newest last, capped so a long-lived log cannot crowd out
 * the context it is meant to protect. Full text stays on disk for `read`.
 */
export function buildDecisionsDigest(decisions: readonly Decision[], cwd: string): string | undefined {
	const active = decisions.filter((d) => d.status === "active");
	if (active.length === 0) return undefined;

	const shown = active.slice(-DIGEST_MAX_DECISIONS);
	const omitted = active.length - shown.length;
	const relDir = join(CONFIG_DIR_NAME, "memory", "decisions");

	const lines = shown.map((d) => `- **${d.title}** (${d.id}): ${firstLine(d.decision, DIGEST_MAX_LINE_LENGTH)}`);
	if (omitted > 0) {
		lines.push(`- (${omitted} older decision${omitted === 1 ? "" : "s"} omitted)`);
	}
	lines.push("", `Full text: ${relDir}/<id>.md (relative to ${cwd}).`);
	return lines.join("\n");
}
